import { supabase } from '@/lib/supabase';
import { TYPE_COMPTE_CLIENT } from '@/lib/accountType';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Réseaux internes (ex. « Zifek Commerce »).
// Le superadmin crée et paramètre des réseaux : titre, description,
// règles, cible (« chez qui » le réseau doit s'afficher) et activation.
// Les commerces / marketplaces peuvent ensuite demander à rejoindre
// un réseau, ce qui les rend « membres » (badge + agrégation publique).
// ─────────────────────────────────────────────────────────────

/** Public visé par l'affichage du réseau. */
export type ReseauCible = 'commerce' | 'marketplace' | 'both';

export const RESEAU_CIBLES: { value: ReseauCible; label: string }[] = [
  { value: 'both', label: 'Commerces et marketplaces' },
  { value: 'commerce', label: 'Commerces uniquement' },
  { value: 'marketplace', label: 'Marketplaces uniquement' },
];

export function normalizeCible(value: unknown): ReseauCible {
  const v = String(value || '').trim();
  return v === 'commerce' || v === 'marketplace' ? v : 'both';
}

export function cibleLabel(cible: ReseauCible): string {
  return RESEAU_CIBLES.find((c) => c.value === cible)?.label || 'Commerces et marketplaces';
}

/** Le réseau s'affiche-t-il pour ce type de compte ? */
export function reseauMatchesRole(cible: ReseauCible, role: 'commerce' | 'marketplace'): boolean {
  return cible === 'both' || cible === role;
}

export interface Reseau {
  id: number;
  titre: string;
  description: string;
  regles: string;
  icon: string;
  cible: ReseauCible;
  actif: boolean;
  sort_order: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ReseauInput {
  titre: string;
  description?: string;
  regles?: string;
  icon?: string;
  cible?: ReseauCible;
  actif?: boolean;
  sort_order?: number;
}

function mapReseau(row: Record<string, unknown>): Reseau {
  return {
    id: Number(row.id),
    titre: (row.titre as string) || '',
    description: (row.description as string) || '',
    regles: (row.regles as string) || '',
    icon: (row.icon as string) || 'ri-share-forward-line',
    cible: normalizeCible(row.cible),
    actif: row.actif !== false,
    sort_order: Number(row.sort_order || 0),
    createdAt: (row.created_at as string) || null,
    updatedAt: (row.updated_at as string) || null,
  };
}

const RESEAU_COLUMNS = 'id, titre, description, regles, icon, cible, actif, sort_order, created_at, updated_at';

// ── Définitions (superadmin) ───────────────────────────────────

/** Tous les réseaux (actifs et inactifs) — usage back-office. */
export async function listReseaux(): Promise<Reseau[]> {
  try {
    const { data, error } = await supabase
      .from('reseaux')
      .select(RESEAU_COLUMNS)
      .order('sort_order', { ascending: true })
      .order('titre', { ascending: true });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map(mapReseau);
  } catch {
    return [];
  }
}

/** Réseaux actifs, éventuellement filtrés par rôle (cible). */
export async function listActiveReseaux(role?: 'commerce' | 'marketplace'): Promise<Reseau[]> {
  const all = await listReseaux();
  return all.filter((r) => r.actif && (!role || reseauMatchesRole(r.cible, role)));
}

export async function createReseau(input: ReseauInput): Promise<void> {
  const { error } = await supabase.from('reseaux').insert({
    titre: input.titre.trim(),
    description: (input.description || '').trim(),
    regles: (input.regles || '').trim(),
    icon: input.icon || 'ri-share-forward-line',
    cible: normalizeCible(input.cible),
    actif: input.actif !== false,
    sort_order: Number(input.sort_order) || 0,
  });
  if (error) throw error;
}

export async function updateReseau(id: number, patch: Partial<ReseauInput>): Promise<void> {
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.titre !== undefined) row.titre = patch.titre.trim();
  if (patch.description !== undefined) row.description = patch.description.trim();
  if (patch.regles !== undefined) row.regles = patch.regles.trim();
  if (patch.icon !== undefined) row.icon = patch.icon;
  if (patch.cible !== undefined) row.cible = normalizeCible(patch.cible);
  if (patch.actif !== undefined) row.actif = patch.actif;
  if (patch.sort_order !== undefined) row.sort_order = Number(patch.sort_order) || 0;
  const { error } = await supabase.from('reseaux').update(row).eq('id', id);
  if (error) throw error;
}

export async function deleteReseau(id: number): Promise<void> {
  const { error } = await supabase.from('reseaux').delete().eq('id', id);
  if (error) throw error;
}

// ── Appartenance (membres) ─────────────────────────────────────

/** Réseaux dont un compte est membre (avec le détail de chaque réseau). */
export async function fetchUserReseaux(userId: number): Promise<Reseau[]> {
  if (!userId) return [];
  try {
    const { data: links, error } = await supabase
      .from('reseau_membres')
      .select('idreseau')
      .eq('iduser', userId);
    if (error || !links) return [];
    const ids = (links as { idreseau: number }[])
      .map((l) => Number(l.idreseau))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) return [];
    const { data, error: rErr } = await supabase
      .from('reseaux')
      .select(RESEAU_COLUMNS)
      .in('id', ids)
      .eq('actif', true);
    if (rErr || !data) return [];
    return (data as Record<string, unknown>[]).map(mapReseau);
  } catch {
    return [];
  }
}

export async function isMember(userId: number, reseauId: number): Promise<boolean> {
  if (!userId || !reseauId) return false;
  try {
    const { data } = await supabase
      .from('reseau_membres')
      .select('id')
      .eq('iduser', userId)
      .eq('idreseau', reseauId)
      .maybeSingle();
    return Boolean(data);
  } catch {
    return false;
  }
}

/**
 * Rejoint un réseau. On synchronise aussi `users.reseau_zifek` pour les
 * réseaux « Zifek » (compatibilité avec l'agrégation historique).
 */
export async function joinReseau(userId: number, reseau: Reseau): Promise<void> {
  if (!userId || !reseau) return;
  const { error } = await supabase
    .from('reseau_membres')
    .insert({ idreseau: reseau.id, iduser: userId });
  if (error && !/duplicate|unique/i.test(error.message)) throw error;
  await syncLegacyFlag(userId);
}

/** Quitte un réseau. */
export async function leaveReseau(userId: number, reseau: Reseau): Promise<void> {
  if (!userId || !reseau) return;
  const { error } = await supabase
    .from('reseau_membres')
    .delete()
    .eq('idreseau', reseau.id)
    .eq('iduser', userId);
  if (error) throw error;
  await syncLegacyFlag(userId);
}

/**
 * Recalcule le drapeau historique `users.reseau_zifek` : 1 dès que le
 * compte est membre d'au moins un réseau « Zifek ».
 */
async function syncLegacyFlag(userId: number): Promise<void> {
  try {
    const memberships = await fetchUserReseaux(userId);
    const inZifek = memberships.some((r) => /zifek/i.test(r.titre));
    await supabase.from('users').update({ reseau_zifek: inZifek ? 1 : 0 }).eq('id', userId);
  } catch {
    /* best-effort */
  }
}

/** Identifiants des membres d'une liste de réseaux (hors soi-même, hors clients). */
export async function fetchNetworkMemberIds(reseauIds: number[], excludeUserId?: number): Promise<number[]> {
  const ids = [...new Set(reseauIds.filter((n) => Number.isFinite(n) && n > 0))];
  if (ids.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('reseau_membres')
      .select('iduser')
      .in('idreseau', ids);
    if (error || !data) return [];
    let userIds = [...new Set((data as { iduser: number }[])
      .map((r) => Number(r.iduser))
      .filter((n) => Number.isFinite(n) && n > 0))];
    if (excludeUserId && Number.isFinite(excludeUserId)) {
      userIds = userIds.filter((n) => n !== Number(excludeUserId));
    }
    if (userIds.length === 0) return [];
    const { data: users } = await supabase
      .from('users')
      .select('id, type, typecompte')
      .in('id', userIds);
    return ((users as { id: number; type: string | null; typecompte: number | null }[] | null) || [])
      .filter((u) => {
        // Exclut les comptes clients : on décide par l'ID du type de compte
        // (6 = Client). Le libellé ne sert que de repli si l'ID est absent.
        const id = Number(u.typecompte);
        if (Number.isFinite(id) && id > 0) return id !== TYPE_COMPTE_CLIENT;
        return (u.type || '').toLowerCase() !== 'client';
      })
      .map((u) => Number(u.id));
  } catch {
    return [];
  }
}

/** Nombre de membres par réseau (carte idreseau → nombre). */
export async function fetchReseauMemberCounts(): Promise<Map<number, number>> {
  const map = new Map<number, number>();
  try {
    const { data, error } = await supabase
      .from('reseau_membres')
      .select('idreseau');
    if (error || !data) return map;
    (data as { idreseau: number }[]).forEach((r) => {
      const key = Number(r.idreseau);
      if (Number.isFinite(key)) map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  } catch {
    return map;
  }
}

/** Identifiants des réseaux auxquels un compte appartient. */
export async function fetchUserReseauIds(userId: number): Promise<number[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('reseau_membres')
      .select('idreseau')
      .eq('iduser', userId);
    if (error || !data) return [];
    return [...new Set((data as { idreseau: number }[])
      .map((r) => Number(r.idreseau))
      .filter((n) => Number.isFinite(n) && n > 0))];
  } catch {
    return [];
  }
}